import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import { ICategoryTarget, IModelAllocationChartConfig } from '../../irb-editors.model';
import { EditorModelService } from '../editor-model.service';
import { Subscription } from 'rxjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
  standalone: false,
  selector: 'irb-model-allocation-chart-group',
  templateUrl: './model-allocation-chart-group.component.html',
  styleUrls: ['./model-allocation-chart-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ModelAllocationChartGroupComponent implements OnInit, OnDestroy {

  @Input() title: string;
  @Input() subTitle: string;
  @Input() modelType: string;
  @Input() noTarget: boolean = false;
  @Input() categoryLabel: string = 'CustCat';
  @Input() expanded: boolean = true;
  @Input() groupBy: string;
  @Output() expandedChange = new EventEmitter<boolean>();

  _config: IModelAllocationChartConfig;
  @Input()
  get config(): IModelAllocationChartConfig { return this._config; }
  set config(c: IModelAllocationChartConfig) {
    this._config = c;
    this.buildChartData();
  }

  categoryTargetPercent: Array<ICategoryTarget> = [];
  chartDataDoughnut: any;
  chartDataBar: any;
  barChartHeight: string;
  colors: Array<string> = [];
  yAxisTooltipPlugin = {
    id: 'yAxisTooltip',
    afterEvent: (chart: any, args: any) => {
      const event = args.event;
      const yScale = chart.scales.y;
      if (event.type === 'mousemove' && yScale) {
        if (event.x < yScale.right && event.y >= yScale.top && event.y <= yScale.bottom) {
          const idx = Math.round(yScale.getValueForPixel(event.y));
          if (idx >= 0 && idx < chart.data.labels.length) {
            chart.canvas.title = chart.data.labels[idx];
          } else {
            chart.canvas.title = '';
          }
        } else {
          chart.canvas.title = '';
        }
      }
    }
  };
  plugins = [ChartDataLabels, this.yAxisTooltipPlugin];

  private editDataChangeSubscription: Subscription;

  doughnutOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const decimal = this.modelType === 'Blended' ? 2 : 1;
            const label = context.label;
            const value = Number(context.dataset.data[context.dataIndex]).toFixed(decimal);
            return this.noTarget ? label : label + ' ' + value + '%';
          }
        }
      }
    }
  };

  barChartOptions: any = {
    indexAxis: 'y',
    responsive: false,
    maintainAspectRatio: false,
    aspectRatio: 0.8,
    barPercentage: 0.8,
    layout: {
        padding: {
            right: 100
        }
    },
    scales: {
      x: {
        ticks: {
          color: "#CCC",
          font: { weight: 500 }
        },
        grid: {
          color: "#CCC",
          drawBorder: false
        },
        position: 'top'
      },
      y: {
        grid: { display: false },
        afterFit: (axis: any) => {
          axis.width = 160;
        },
        ticks: {
          callback: function(value: any) {
            const label = this.getLabelForValue(value) || '';
            return label.length > 25 ? label.substring(0, 22) + '...' : label;
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        labels: {
          primary: {
            anchor: 'end',
            align: 'right',
            offset: 10,
            display: true,
            backgroundColor: "#EEE",
            borderRadius: 2,
            font: {size: 9},
            formatter: (value: any) => {
              return value > 0 ? value + '%' : '';
            }
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const decimal = this.modelType === 'Blended' ? 2 : 1;
            const label = context.label;
            const value = Number(context.dataset.data[context.dataIndex]).toFixed(decimal);
            return label + ' ' + value + '%';
          }
        }
      }
    }
  };

  constructor(private editorModelService: EditorModelService) {}

  ngOnInit(): void {
    this.buildChartData();
    this.editDataChangeSubscription = this.editorModelService.parametersChanged.subscribe(() => {
      this.buildChartData();
    });
  }

  buildChartData() {
    this.colors = this.editorModelService.getChatColors();

    if (this.groupBy) {
      this.categoryTargetPercent = this.editorModelService.getGroupedAllocations(this.groupBy);
    } else {
      this.categoryTargetPercent = this.editorModelService.getCategoryTargetPercent();
    }

    if (!this.categoryTargetPercent || this.categoryTargetPercent.length === 0) {
      this.colors = ['#93000F'];
      this.categoryTargetPercent = [{ category: 'Under-Allocated', targetPercent: 100, colorCode: '#93000F' }];
    }

    if (!this.noTarget) {
      const globSet: any = this.editorModelService.getGlobalSettings();
      if (globSet && globSet.securityCustAttrAbbr) {
        this.categoryLabel = globSet.securityCustAttrAbbr;
      }
    }

    const categories = this.categoryTargetPercent.map(item => item.category);
    const percentages = this.categoryTargetPercent.map(item => Number(item.targetPercent));
    const bgColors = this.categoryTargetPercent.map(item => item.colorCode || '#BABABA');
    this.barChartHeight = 40 + Math.max(40, categories.length * 40) + 'px';

    // Doughnut chart data
    this.chartDataDoughnut = {
      labels: categories,
      datasets: [{
        data: percentages,
        backgroundColor: bgColors,
        hoverBackgroundColor: bgColors,
        borderWidth: 2,
        hoverBorderColor: ['#FFFFFF']
      }]
    };

    // Bar chart data
    this.chartDataBar = {
      labels: categories,
      datasets: [{
        label: 'Target %',
        data: percentages,
        backgroundColor: bgColors,
        borderWidth: 1
      }]
    };
  }

  onExpandedChange() {
    if(this.expanded) {
      setTimeout(() => this.buildChartData());
    }
    this.expandedChange.emit(this.expanded);
  }

  getColorByCategory(category: string): string {
    return this.editorModelService.getColorByCategory(category);
  }

  ngOnDestroy(): void {
    if (this.editDataChangeSubscription) {
      this.editDataChangeSubscription.unsubscribe();
    }
  }
}
